// BUILD-042 — My HR visual pass (2026-10-05, Owner-directed). Section bodies for the My HR tab
// list. Job and Report-to render REAL data already returned by GET /hr/me/record (no new
// backend call). Personal Details, Immigration, and Salary have NO backend table yet — per
// Owner direction ("if you have no code for some of this, I want to visually see the
// information for now, and we can link/code to it after... 100% test mode, will not affect any
// company information, no real users"), these render real, interactive fields so the LOOK/FEEL/
// LABELS/FIELD SET can be reviewed now, holding their values in local React state only. Each
// says so in one small inline note, not a loud banner — this is deliberately lighter than
// CollapsibleHelp's disclosure convention because it is a known, temporary, Owner-acknowledged
// state for this review pass, not a safety-relevant disclosure. Nothing in this file calls a
// write API; reloading the page resets these sections.
//
// Qualifications and Memberships moved to their own file, MyHrQualifications.tsx, as of the
// 2026-10-06 17:00 addendum — that screen's action model (Add/Edit/Archive/Restore vs. Submit/
// Renew/Request-correction vs. View/Request-correction, verification status, expandable
// history) is substantially different from this file's simpler governed-fact-request pattern,
// so it earned its own file rather than growing this one further.
import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { useMyHrViewerRole } from './MyHrViewerRoleContext';
import { useHrRequests } from './HrRequestsContext';
import { CardTitle } from './workspace/SectionHeading';
import { LocalDemoNote } from './workspace/LocalDemoNote';
import { formatLocalDate } from './hrFormat';
import { useDepartmentNames } from './useDepartmentNames';

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={htmlFor} className="text-xs text-gray-500">{label}</label>
      {children}
    </div>
  );
}

const inputClass = 'border border-gray-300 rounded px-3 py-1.5 text-sm';

/** A single clean label/value pair for the read-only Personal Details grid. Value is bold,
 *  matching the approved reference's field-value weight ("Position / Project Coordinator"). */
function InfoItem({ label, value, badge }: { label: string; value: string; badge?: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-sm font-bold text-gray-900 mt-0.5 flex items-center gap-2">
        {value || <span className="text-gray-400 font-normal">Not on file</span>}
        {badge}
      </dd>
    </div>
  );
}

// ── Personal Details ─────────────────────────────────────────────────────────────────────────
// BUILD-042 My HR self-service & change-request decision (2026-10-06): legal name and birth
// date are GOVERNED FACTS — "legal name, birth-date corrections and official identification use
// request/review semantics." Owner visual correction (2026-10-07): the prior layout mixed
// inline per-field request cards and disabled-looking input fields in one dense block, which
// read as cluttered and implied an employee could type into fields they actually cannot edit.
// Rebuilt as a single clean read-only grid (Full Name, Employee ID, Preferred Name, Date of
// Birth, Other ID, Driver's License, License Expiry, Nationality, Marital Status, Gender) with
// ONE "Request a correction" control at the very bottom — clicking it reveals a picker limited
// to the fields that are ACTUALLY correctable today (Full Name, Date of Birth), never a free
// list of every displayed field. The employee view has no data-entry fields at all, per Chris's
// direction; HR Administrator view additionally gets inline editing for the non-governed
// "other details" fields (Other ID/License/Nationality/Marital Status/Gender), since
// maintaining those on an Employee's behalf is an HR Administration action, not a self-service
// one — those are not governed facts, so no request/approval step applies to them.
export function PersonalDetailsSection({ employeeId, employeeName, legalName, preferredName, forceAdmin }: { employeeId: string; employeeName: string; legalName: string; preferredName: string | null; forceAdmin?: boolean }) {
  // 2026-10-13 UAT: the HR Administration employee workspace now reuses the same four sub-tabs as
  // the employee self view (Personal details / Contact details / Emergency contacts / Immigration)
  // so the two views match. On the admin side there is no "Viewing as" toggle, so admin mode is
  // passed explicitly via forceAdmin rather than read from the (self-only) viewer-role context.
  const { role } = useMyHrViewerRole();
  const isAdmin = forceAdmin ?? role === 'hrAdmin';
  const [form, setForm] = useState({
    otherId: '', driversLicenseNumber: '', licenseExpiryDate: '',
    nationality: '', maritalStatus: '', gender: '',
  });
  const licenseExpId = useId();
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const { myRequests } = useHrRequests();
  // Legal name is no longer requestable (parked, see below). A request submitted before that change still shows its status on the name.
  const nameRequest = myRequests.find((r) => r.employeeId === employeeId && r.fieldKey === 'legal_name');

  function pendingBadge(req: typeof nameRequest) {
    if (!req) return null;
    if (req.status === 'open') return <span className="text-[11px] bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">Awaiting HR review</span>;
    if (req.status === 'resolved') return <span className="text-[11px] bg-green-50 text-green-700 border border-green-200 rounded-full px-2 py-0.5">Correction resolved</span>;
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
      {/* 2026-10-13 UAT: all four Personal & Contacts sub-tab panels use the SAME CardTitle
          heading treatment for consistency (Personal details / Contact details / Emergency
          contacts / Immigration). */}
      <CardTitle>Personal details</CardTitle>
      <div className="space-y-5">
        {/* Row 1: Full Name, Preferred Name, Date of Birth */}
        <dl className="grid grid-cols-3 gap-5">
          <InfoItem label="Full Name" value={legalName} badge={pendingBadge(nameRequest)} />
          <InfoItem label="Preferred Name" value={preferredName || legalName} />
          <InfoItem label="Date of Birth" value="Not on file" />
        </dl>

        {/* Row 2: Employee ID, Other ID */}
        <dl className="grid grid-cols-2 gap-5">
          <InfoItem label="Employee ID" value={employeeId} />
          {!isAdmin && <InfoItem label="Other ID" value={form.otherId} />}
        </dl>

        {!isAdmin && (
          <>
            {/* Row 3: Driver's License Number & Expiry Date — kept together, same row. */}
            <dl className="grid grid-cols-2 gap-5">
              <InfoItem label="Driver's License Number" value={form.driversLicenseNumber} />
              <InfoItem label="License Expiry Date" value={form.licenseExpiryDate} />
            </dl>

            {/* Row 4: Nationality, Marital Status, Gender */}
            <dl className="grid grid-cols-3 gap-5">
              <InfoItem label="Nationality" value={form.nationality} />
              <InfoItem label="Marital Status" value={form.maritalStatus} />
              <InfoItem label="Gender" value={form.gender} />
            </dl>
          </>
        )}
      </div>

      {isAdmin && (
        <div className="mt-5 pt-4 border-t border-gray-100">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Other Details (HR Administration)</h3>
          <LocalDemoNote text="Local demonstration only — there is no backend for these fields yet. The Save button below does not persist anything; reloading the page resets it." />
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Other ID" htmlFor="pd-oid"><input id="pd-oid" className={inputClass} value={form.otherId} onChange={set('otherId')} /></Field>
            </div>
            {/* Driver's license number & expiry date — kept together, same row. */}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Driver's license number" htmlFor="pd-dl"><input id="pd-dl" className={inputClass} value={form.driversLicenseNumber} onChange={set('driversLicenseNumber')} /></Field>
              <Field label="License expiry date" htmlFor={licenseExpId}><input id={licenseExpId} type="date" className={inputClass} value={form.licenseExpiryDate} onChange={set('licenseExpiryDate')} /></Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Nationality" htmlFor="pd-nat">
                <select id="pd-nat" className={inputClass} value={form.nationality} onChange={set('nationality')}>
                  <option value="">Select…</option>
                  <option>American</option><option>Canadian</option><option>Mexican</option><option>Other</option>
                </select>
              </Field>
              <Field label="Marital status" htmlFor="pd-marital">
                <select id="pd-marital" className={inputClass} value={form.maritalStatus} onChange={set('maritalStatus')}>
                  <option value="">Select…</option>
                  <option>Single</option><option>Married</option><option>Divorced</option><option>Widowed</option>
                </select>
              </Field>
              <Field label="Gender" htmlFor="pd-gender">
                <div className="flex items-center gap-4 h-full pt-1.5">
                  {['Male', 'Female', 'Other'].map((g) => (
                    <label key={g} className="flex items-center gap-1.5 text-sm text-gray-700">
                      <input type="radio" name="pd-gender" checked={form.gender === g} onChange={() => setForm((f) => ({ ...f, gender: g }))} />
                      {g}
                    </label>
                  ))}
                </div>
              </Field>
            </div>
          </div>
          <button type="button" className="mt-4 bg-[#4AABDE] text-white text-sm px-4 py-1.5 rounded">
            Save
          </button>
        </div>
      )}

      {/* Oct 19 06:28: HR keeps NO separate legal-name record. The name shown here comes from the platform profile, so there is nothing for
          HR to correct and no correction request is offered for it (a request that no HR screen can fulfil would only strand the employee). */}
      {!isAdmin && (
        <p className="mt-5 pt-4 border-t border-gray-100 text-xs text-gray-500" data-testid="name-from-profile-note">
          Your name comes from your platform profile, not from an HR record, so it has no HR correction request.
        </p>
      )}
    </div>
  );
}

/** Shared, inconspicuous bottom-of-card "Request a correction" control for governed facts —
 *  used by Personal Details, Job and Report-to. Collapsed by default to a single small link,
 *  never a prominent button, so it reads as a secondary action rather than competing with the
 *  clean read-only display above it.
 *
 *  The picker offers ONLY the governed fields the SERVER allows a correction request for (its
 *  fixed allow-list, delivered by GET /hr/me/correction-requests) intersected with the fields
 *  present on this card. A field the server does not govern is never shown here — there is no
 *  generic field write path (Oct 19 appendix correction #1). A field with an OPEN request shows
 *  that instead of letting a second request stack on top of it (the server also enforces one
 *  open request per field). The server derives the authoritative current value itself; the
 *  `currentValue` passed here is used only for the "was → proposed" display. */
export function GovernedFactsCorrectionPicker({
  fields,
}: { fields: Array<{ fieldKey: string; currentValue: string }> }) {
  const { myRequests, correctableFields, submitRequest } = useHrRequests();
  const [open, setOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState('');
  const [proposedValue, setProposedValue] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Only fields that are BOTH on this card and in the server allow-list can be requested.
  const allowed = correctableFields.filter((cf) => fields.some((f) => f.fieldKey === cf.key));
  if (allowed.length === 0) return null;

  const selected = allowed.find((f) => f.key === selectedKey);
  const openForSelected = selected ? myRequests.find((r) => r.fieldKey === selected.key && r.status === 'open') : null;

  function toggleOpen() {
    setOpen((v) => !v);
    setSelectedKey('');
    setProposedValue('');
    setReason('');
    setError(null);
  }
  async function submit() {
    if (!selected || !proposedValue.trim() || !reason.trim()) return;
    setError(null);
    const res = await submitRequest({ fieldKey: selected.key, proposedValue: proposedValue.trim(), reason: reason.trim() });
    if (res.ok) setOpen(false);
    else setError(res.status === 409 ? `You already have an open request for ${selected.label}. Wait for HR to resolve it before submitting another.` : (res.message || 'Could not submit the request.'));
  }

  return (
    <div className="mt-5 pt-4 border-t border-gray-100">
      <button type="button" onClick={toggleOpen} className="text-sm text-[#4AABDE] hover:underline" data-testid="correction-picker-toggle">
        {open ? 'Cancel' : 'Request a correction'}
      </button>
      {open && (
        <div className="mt-3 space-y-2 max-w-sm">
          <div>
            <label htmlFor="pd-correction-field" className="text-xs text-gray-500">Which field?</label>
            <select
              id="pd-correction-field"
              data-testid="correction-field"
              className={inputClass + ' w-full'}
              value={selectedKey}
              onChange={(e) => { setSelectedKey(e.target.value); setProposedValue(''); setReason(''); setError(null); }}
            >
              <option value="">Select…</option>
              {allowed.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </div>
          {selected && openForSelected && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5" data-testid="correction-already-open">
              A correction to {selected.label} is already awaiting HR review. No action needed from you.
            </div>
          )}
          {selected && !openForSelected && (
            <>
              <div>
                <label htmlFor="pd-correction-value" className="text-xs text-gray-500">Proposed value</label>
                <input id="pd-correction-value" data-testid="correction-value" className={inputClass + ' w-full'} value={proposedValue} onChange={(e) => setProposedValue(e.target.value)} />
              </div>
              <div>
                <label htmlFor="pd-correction-reason" className="text-xs text-gray-500">Reason</label>
                <textarea id="pd-correction-reason" data-testid="correction-reason" className={inputClass + ' w-full'} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
              </div>
              {error && <div className="text-xs text-red-600" role="alert" data-testid="correction-submit-error">{error}</div>}
              <button type="button" onClick={submit} disabled={!proposedValue.trim() || !reason.trim()} data-testid="correction-submit" className="bg-[#4AABDE] text-white text-sm px-4 py-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed">
                Submit request
              </button>
              <p className="text-[11px] text-gray-400">This goes to HR for review. Nothing on your profile changes until HR makes the change.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Immigration ("applies to some employees" — kept per Owner direction) ────────────────────────
export function ImmigrationSection() {
  const [form, setForm] = useState({ status: '', workPermitNumber: '', permitExpiryDate: '', visaType: '', notes: '' });
  const expId = useId();
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
      <CardTitle>Immigration</CardTitle>
      <LocalDemoNote text="Local demonstration only — there is no backend for this section yet. The Save button below does not persist anything; reloading the page resets it." />
      <div className="grid grid-cols-2 gap-3 mt-2">
        <Field label="Immigration status" htmlFor="im-status">
          <select id="im-status" className={inputClass} value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
            <option value="">Not applicable</option>
            <option>Citizen</option><option>Permanent resident</option><option>Work visa</option><option>Pending</option>
          </select>
        </Field>
        <Field label="Visa type" htmlFor="im-visa"><input id="im-visa" className={inputClass} value={form.visaType} onChange={(e) => setForm((f) => ({ ...f, visaType: e.target.value }))} /></Field>
        <Field label="Work permit number" htmlFor="im-permit"><input id="im-permit" className={inputClass} value={form.workPermitNumber} onChange={(e) => setForm((f) => ({ ...f, workPermitNumber: e.target.value }))} /></Field>
        <Field label="Permit expiry date" htmlFor={expId}><input id={expId} type="date" className={inputClass} value={form.permitExpiryDate} onChange={(e) => setForm((f) => ({ ...f, permitExpiryDate: e.target.value }))} /></Field>
        <div className="col-span-2">
          <Field label="Notes" htmlFor="im-notes"><textarea id="im-notes" className={inputClass} rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></Field>
        </div>
      </div>
      <button type="button" className="mt-4 bg-[#4AABDE] text-white text-sm px-4 py-1.5 rounded">
        Save
      </button>
    </div>
  );
}

// ── Job (REAL data — current term from GET /hr/me/record, no new backend call) ─────────────────
// BUILD-042 My HR self-service & change-request decision (2026-10-06): title/manager/department/
// hire date/employment status are authorized administrative/approval actions; employee
// correction requests are proposals only — rendered via RequestCorrectionCard, current value
// always from the REAL record.
interface JobTerm {
  title: string | null; departmentId: string | null; classification: string | null;
}
// Yes — per the governing decision, Title, Department, Hire Date, and Employment Status are
// all explicitly named governed facts where "employee correction requests are proposals only."
// Classification is NOT named in that list, so it stays plain view-only with no correction
// option at all, same as before. Rebuilt (2026-10-07, Owner request) to match Personal Details'
// clean read-only row layout, with ONE inconspicuous "Request a correction" link at the bottom
// instead of a RequestCorrectionCard per field — same GovernedFactsCorrectionPicker Personal
// Details uses, so the picker is limited to exactly these four correctable fields.
export function JobSection({
  employeeId, employeeName, current, employmentStatus, hireDate,
}: {
  employeeId: string;
  employeeName: string;
  current: JobTerm | null;
  employmentStatus: string | undefined;
  hireDate: string | undefined;
}) {
  // Oct 20: the department is shown by NAME (this used to print the stored id). A disabled department still shows its name.
  const departmentName = useDepartmentNames().nameOf(current?.departmentId);
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
      <CardTitle>Position &amp; status</CardTitle>

      <div className="space-y-5">
        {/* Row 1: Title, Department */}
        <dl className="grid grid-cols-2 gap-5">
          <InfoItem label="Title" value={current?.title ?? ''} />
          <InfoItem label="Department" value={departmentName ?? ''} />
        </dl>

        {/* Row 2: Hire Date, Employment Status. October 10 22:28 addendum: hire date was
            previously rendered as the raw stored YYYY-MM-DD string with no formatting at all —
            now MM/DD/YYYY via the same date-only-safe formatter used elsewhere in HR. Display
            only; the stored value and the correction-request "currentValue" comparison field
            below are unchanged. */}
        <dl className="grid grid-cols-2 gap-5">
          <InfoItem label="Hire Date" value={hireDate ? formatLocalDate(hireDate) : ''} />
          <InfoItem label="Employment Status" value={employmentStatus ?? ''} />
        </dl>

        {/* Row 3: Classification — not a governed fact per the decision, no correction option. */}
        <dl className="grid grid-cols-2 gap-5">
          <InfoItem label="Classification" value={current?.classification ?? ''} />
        </dl>
      </div>

      <GovernedFactsCorrectionPicker
        fields={[
          { fieldKey: 'title', currentValue: current?.title ?? '—' },
          { fieldKey: 'department', currentValue: departmentName ?? '—' },
          { fieldKey: 'hire_date', currentValue: hireDate ? formatLocalDate(hireDate) : '—' },
          { fieldKey: 'employment_status', currentValue: employmentStatus ?? '—' },
        ]}
      />
    </div>
  );
}

// ── Salary ────────────────────────────────────────────────────────────────────────────────────
// Compensation and PTO entitlement are authorized administrative/approval actions; employee
// correction requests are proposals only. Same clean row layout + inconspicuous bottom
// correction picker as Job (2026-10-07, Owner request: "same format for salary").
export function SalarySection({ employeeId, employeeName }: { employeeId: string; employeeName: string }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
      <CardTitle>Compensation</CardTitle>
      <LocalDemoNote text="Local demonstration only — there is no backend for compensation data yet. Values below are placeholders, not your real pay." />

      <div className="space-y-5 mt-2">
        {/* Row 1: Pay Rate, Pay Group */}
        <dl className="grid grid-cols-2 gap-5">
          <InfoItem label="Pay Rate" value="" />
          <InfoItem label="Pay Group" value="" />
        </dl>

        {/* Row 2: PTO Entitlement */}
        <dl className="grid grid-cols-2 gap-5">
          <InfoItem label="PTO Entitlement" value="" />
        </dl>
      </div>

      {/* Compensation fields are not on the server's governed correction allow-list, so no
          correction-request control is offered here (there is no generic field write path). */}
    </div>
  );
}

// ── Report-to (REAL data — manager, from GET /hr/me/record) ─────────────────────────────────────
// Same clean row + inconspicuous bottom correction picker as Job/Salary (2026-10-07 pass).
// Manager is a governed fact — employee correction requests are proposals only.
export function ReportToSection({
  employeeId, employeeName, managerName, managerEmployeeId,
}: { employeeId: string; employeeName: string; managerName: string | null; managerEmployeeId: string | null }) {
  const currentManager = managerName ?? managerEmployeeId ?? 'No manager on file';
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
      <CardTitle>Manager &amp; team</CardTitle>

      <dl className="grid grid-cols-2 gap-5">
        <InfoItem label="Manager" value={currentManager} />
      </dl>

      <GovernedFactsCorrectionPicker
        fields={[
          { fieldKey: 'manager', currentValue: currentManager },
        ]}
      />
    </div>
  );
}
